<template>
    <v-container>
      <v-row>
        <v-col v-if="response != null" cols="12" md="9">
          <v-card class="pa-4">
            <v-tabs v-model="activeTab" class="py-4" background-color="primary">
              <v-tab>{{ $t("drivers") }}</v-tab>
              <v-tab>{{ $t("incident") }}</v-tab>
            </v-tabs>
  
            <v-tabs-items v-model="activeTab">
              <v-tab-item>
                <div class="local-table data-container">
                  <v-data-table
                    :headers="driverHeaders"
                    :items="response"
                    dense
                    class="font-weight-light"
                    hide-default-footer
                    @click:row="onDriverClick"
                    :items-per-page="response.length"
                    mobile-breakpoint
                  ></v-data-table>
                </div>
              </v-tab-item>
  
              <v-tab-item>
                <div class="local-table data-container" @scroll="handleIncidentsDataScroll">
                  <v-data-table
                    :headers="incidentHeaders"
                    :items="incidentsData"
                    dense
                    hide-default-footer
                    :items-per-page="incidentsData.length"
                  >
                  <template v-if="isSuperAdmin" #header.isValid>
                    <div class="d-flex align-center justify-center header-is-valid">
                      <span class="mr-2">{{ $t('isValid') }}</span>
                      <v-checkbox
                        :input-value="allValid"
                        @change="toggleAllIsValid"
                        hide-details
                        class="header-is-valid-checkbox"
                        dense
                      />
                    </div>
                  </template>

                    <template #item.showOnMap="{ item }">
                      <div v-if="item.latitude != undefined && item.longitude != undefined">
                        <a :href="`https://www.google.com/maps/place/${item.latitude},${item.longitude}?hl=sr`" target="_blank">{{ $t('showOnMap') }}</a>
                      </div>
                    </template>

                    <template v-if="isSuperAdmin" #item.isValid="{ item }">
                      <div v-if="item.incidentId > 0">
                        <v-checkbox
                          v-model="item.isValid"
                          @change="handleIsValidChange(item)"
                          hide-details
                        />
                      </div>
                    </template>
                  </v-data-table>
                  
                </div>
              </v-tab-item>
  
            </v-tabs-items>

            <div class="my-5">
              <v-btn
                v-if="saveEditedRowsButtonVisible"
                color="primary"
                class="mr-2 font-weight-light"
                small
                @click="saveEditedRows"
              >
                {{ $t('save') }}
              </v-btn>
              <v-btn color="success" :disabled="response == null" small @click="exportAsExcel" class="mr-2 font-weight-light">Excel</v-btn>
              <v-btn color="success" :disabled="response == null" small @click="exportAsPdf" class="mr-2 font-weight-light">PDF</v-btn>
          </div>
          </v-card>
        </v-col>
  
        <v-col cols="12" md="3" :offset-md="response == null ? 9 : 0">
          <v-card class="px-4 py-2">
            <v-card-text>
              <h4 class="font-weight-light pt-3">{{ $t('mapFilters.Options') }} </h4>
  
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <form @submit.prevent="handleSubmit(fetchFilteredData)">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="Drivers"
                    rules="required"
                  >
                    <VSelectAll
                      v-model="searchObject.selectedDrivers"
                      :items="allDrivers"
                      item-text="name"
                      item-value="id"
                      multiple
                      :label="$t('drivers')"
                      outlined
                      dense
                      :error-messages="errors"
                      hide-details="auto"
                    ></VSelectAll>
                  </ValidationProvider>
  
                  <ValidationProvider
                    v-slot="{ validate, errors }"
                    :name="'startDate'"
                    vid="startDate"
                    rules="required"
                  >
                    <v-date-field
                      color="primary"
                      dense
                      hide-details="auto"
                      outlined
                      v-model="searchObject.selectedStartDate"
                      @input="setStartDate"
                      :label="$t('startDate')"
                      id="startDate"
                      :error-messages="errors"
                    ></v-date-field>
                  </ValidationProvider>

                  <ValidationProvider
                    v-slot="{ validate, errors }"
                    :name="'endDate'"
                    vid="endDate"
                    rules="required"
                  >
                    <v-date-field
                      color="primary"
                      dense
                      hide-details="auto"
                      outlined
                      v-model="searchObject.selectedEndDate"
                      @input="setEndDate"
                      :label="$t('endDate')"
                      :minValue="minEndDate"
                      :maxValue="maxEndDate"
                      id="endDate"
                      :error-messages="errors"
                    ></v-date-field>
                  </ValidationProvider>

                  <div class="text-right my-4">
                    <v-btn color="primary" small type="submit" :loading="loadingRequest" :disabled="loadingRequest">
                      {{ $t('search') }}
                    </v-btn>
                  </div>
                </form>
              </ValidationObserver>
  
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </template>
<script>
import store from '@/store/store'
import httpClient from '../api/httpClient'
import { checkIfDateIsAlreadyFormatted } from '../utils/dateHelper'
import formatDate from '@/utils/formatDateHelper.js'
import { replaceSerbianChars } from '../utils/pdfHelper'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
const Excel = require('exceljs')
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import translate from '@/utils/genTable/multilanguageHelper.js'

export default {
    name: 'DriversRankingPage',
    components: {
        ValidationObserver,
        ValidationProvider
    },
    data() {
        return {
            activeTab: 0,
            searchObject: {
                selectedDrivers: [],
                selectedStartDate: null,
                selectedEndDate: null,
            },
            selectedDriver: null,
            allDrivers: [],
            driverHeaders: [
                { text: this.$t('driver'), value: 'driverName' },
                { text: this.$t('negativePoints'), value: 'negativePoints'}
            ],
            incidentsData: [],
            allIncidents: [],
            incidentsPerPage: 50,
            incidentsPage: 1,
            loadingRequest: false,
            response: null,
            editedRows: [],
            userData: JSON.parse(store.getters['user/jwtData'].UserData),
            incidentsInitialState: [],
            minEndDate: null,
            maxEndDate: null
        }
    },
    async mounted() {
        await this.loadDrivers()
    },
    computed: {
      isSuperAdmin() {
        return this.userData && this.userData.IsAdmin 
      },
      incidentHeaders() {
        const headers = [
          { text: this.$t('reportHeaders.Timestamp'), value: 'timestamp' },
          { text: this.$t('incidentType.Incident type'), value: 'incidentName' },
          { text: this.$t('incidentType.Incident value'), value: 'incidentValue' },
          { text: this.$t('graphs.Speed'), value: 'speed' },
          { text: this.$t('showOnMap'), value: 'showOnMap' },
        ]
        if (this.isSuperAdmin) {
          headers.push({ text: this.$t('isValid'), value: 'isValid', sortable: false })
        }
        return headers
      },
      saveEditedRowsButtonVisible() {
        return this.editedRows.length > 0
      },
      allValid: {
        get() {
          return this.allIncidents.length && this.allIncidents.every((item) => item.isValid)
        },
        set(value) {
          if (!this.allIncidents.length) {
            return;
          }

          this.allIncidents.forEach(item => {
            item.isValid = value;
          });
        }
      }
    },  
    methods: {
      async loadDrivers() {
          httpClient.get('/drivers').then(response => {
              for (let data of response.data) {
              this.allDrivers.push({ text: data.name, value: data.id })
              }
          })
      },
      async fetchFilteredData() {
          this.loadingRequest = true

          var url = "/reports/GetDriversRanking?"
          for (var driverId of this.searchObject.selectedDrivers) {
              url += `DriverIds=${driverId}&`
          }

          url += `StartDate=${this.searchObject.selectedStartDate}&EndDate=${this.searchObject.selectedEndDate}`

          await httpClient.get(url)
              .then(response => {
                  if (response.data.hasOwnProperty("reportIsScheduled")) {
                    store.dispatch(
                      'notification/add',
                      {
                        type: 'warning',
                        message: translate('reportQueued', 'errors', true)
                      },
                      {
                        root: true
                      }
                    )

                    this.response = null

                    return
                  }

                  this.response = response.data

                  this.activeTab = 0
                  this.formatTimestamps(this.response)
              })
          
          this.loadingRequest = false
      },
      onDriverClick(driver) {
          if (driver.incidents.length) {
              this.selectedDriver = driver
              this.allIncidents = [...this.selectedDriver.incidents]
              this.incidentsData = this.allIncidents.slice(0, this.incidentsPerPage)
              this.incidentsPage = 1

              this.activeTab = 1
              this.incidentsInitialState = this.allIncidents.map((item) => ({
                incidentId: item.incidentId,
                isValid: item.isValid,
              }))
          }
      },
      handleIncidentsDataScroll(event) {
          const { scrollTop, scrollHeight, clientHeight } = event.target;

          if (scrollTop + clientHeight >= scrollHeight - 50) {
              this.loadMoreIncidents();
          }
      },
      loadMoreIncidents() {
          if (this.incidentsData.length >= this.allIncidents.length) return;

          this.incidentsPage++;
          const nextBatch = this.allIncidents.slice(
              (this.incidentsPage - 1) * this.incidentsPerPage,
              this.incidentsPage * this.incidentsPerPage
          );
          this.incidentsData.push(...nextBatch);
      },
      formatTimestamps(drivers) {
        for (const driver of drivers) {
          for (const incident of driver.incidents) {
            if (incident.isFromZobs) {
              if (checkIfDateIsAlreadyFormatted(incident.startDate)) {
                continue
              }

              incident.startDate = formatDate(incident.startDate, 'long', true)
              incident.endDate = formatDate(incident.endDate, 'long', true)
              incident.timestamp = `${incident.startDate} - ${incident.endDate}`
              continue
            }
            
            if (checkIfDateIsAlreadyFormatted(incident.timestamp)) {
              continue
            }

            incident.timestamp = formatDate(incident.timestamp, 'long', true)
          }
        }
      },  
      setStartDate(date) {
          this.searchObject.selectedStartDate = date
          let startDate = new Date(date)
          this.minEndDate = new Date(startDate.setDate(startDate.getDate() + 1)).toISOString()
          this.maxEndDate = new Date(startDate.setMonth(startDate.getMonth() + 1)).toISOString()
      },
      setEndDate(date) {
          this.searchObject.selectedEndDate = date
      },
      handleIsValidChange(item) {
        const originalRow = this.incidentsInitialState.find((row) => row.incidentId === item.incidentId)
        if (item.isValid === originalRow.isValid) {
          this.editedRows = this.editedRows.filter((row) => row.incidentId !== item.incidentId)
        } else {
          const existingIndex = this.editedRows.findIndex((row) => row.incidentId === item.incidentId)
          if (existingIndex > -1) {
            this.editedRows[existingIndex].isValid = item.isValid
          } else {
            this.editedRows.push({ incidentId: item.incidentId, isValid: item.isValid })
          }
        }
      },
      saveEditedRows() {
        if (!this.editedRows.length) {
          return
        }

        httpClient.put('/reports/ModifyIncidents', this.editedRows)
          .then(() => {
            this.editedRows = []
            store.dispatch('notification/add', { type: 'success', message: this.$t('apiSuccessMsg') }, { root: true })
          })
          .catch(() => {
            store.dispatch('notification/add', {type: 'error', message: that.$t('optimization.serverError')}, { root: true })
          })

        this.incidentsInitialState = this.editedRows
      },
      toggleAllIsValid(value) {
        this.allValid = value

        this.editedRows = this.allIncidents
          .filter(item => {
            const originalRow = this.incidentsInitialState.find(row => row.incidentId === item.incidentId);
            return originalRow && item.isValid !== originalRow.isValid
          })
          .map(item => ({ incidentId: item.incidentId, isValid: item.isValid }))
      },
      async exportAsExcel() {
        const workbook = new Excel.Workbook()
        workbook.creator = 'Puregreen'
        workbook.lastModifiedBy = 'Puregreen'

        const startDate = new Date(this.searchObject.selectedStartDate)
        const endDate = new Date(this.searchObject.selectedEndDate)

        const mainSheet = workbook.addWorksheet(this.$t('scoreboard'))

        mainSheet.addRow([
          this.$t('startDate'),
          `${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()} 00:00:00`,
        ])
        mainSheet.addRow([
          this.$t('endDate'),
          `${endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()} 00:00:00`,
        ])
        mainSheet.addRow([])
        mainSheet.addRow([
          this.$t('driver'),
          this.$t('mileage'),
          this.$t('incidentType.HarshAcceleration'),
          this.$t('incidentType.HarshBreaking'),
          this.$t('incidentType.HarshCornering'),
          this.$t('incidentType.Speeding'),
          this.$t('drivingTimeExceeded'),
          this.$t('scoreboard'),
        ])

        for (const driver of this.response) {
          const row = mainSheet.addRow([
            driver.driverName,
            driver.totalDistanceKm,
            driver.totalHarshAccelerationIncidents,
            driver.totalHarshBreakingIncidents,
            driver.totalHarshCorneringIncidents,
            driver.speedingViolationPoints,
            driver.drivingTimeExceeded,
            driver.negativePoints,
          ])

          const negativePoints = parseFloat(driver.negativePoints.replace(",", "."))

          let bgColor = 'A5D6A7'

          if (!isNaN(negativePoints)) {
            if (negativePoints > 1.5) {
              bgColor = 'EF9A9A'
            } else if (negativePoints >= 1) {
              bgColor = 'FFF59D'
            }
          }
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: bgColor },
            }
          })
        }

        mainSheet.columns = Array(7).fill({ width: 30 })

        for (const driver of this.response) { 
          let sheetName = driver.driverName;
          let counter = 2;

          while (workbook.worksheets.some(sheet => sheet.name === sheetName)) {
            sheetName = `${driver.driverName} ${counter}`
            counter++
          } 

          const driverSheet = workbook.addWorksheet(sheetName)

          driverSheet.addRow([this.$t('driver'), driver.driverName])
          driverSheet.addRow([
            this.$t('startDate'),
            `${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()} 00:00:00`,
          ])
          driverSheet.addRow([
            this.$t('endDate'),
            `${endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()} 00:00:00`,
          ])
          driverSheet.addRow([this.$t('negativePoints'), driver.negativePoints])
          driverSheet.addRow([])
          driverSheet.addRow([
            this.$t('reportHeaders.Timestamp'),
            this.$t('incidentType.Incident type'),
            this.$t('graphs.Speed'),
            this.$t('incidentType.Incident value'),
            this.$t('isValid'),
            this.$t('showOnMap'),
          ])

          for (const incident of driver.incidents) {
            const row = driverSheet.addRow([
              incident.timestamp,
              incident.incidentName,
              incident.speed,
              incident.incidentValue,
              incident.isFromZobs ? '' : incident.isValid ? 'Da' : 'Ne',
              incident.isFromZobs ? '' : `https://www.google.com/maps/place/${incident.latitude},${incident.longitude}?hl=sr`,
            ])

            if (!incident.isFromZobs) {
              const linkCell = row.getCell(6) 
              linkCell.value = {
                text: this.$t('showOnMap'),
                hyperlink: `https://www.google.com/maps/place/${incident.latitude},${incident.longitude}?hl=sr`,
              }
              linkCell.font = {
                color: { argb: '0000FF' },
                underline: true,
              }
            }
          }

          driverSheet.columns = Array(6).fill({ width: 30 })
        }

        const buffer = await workbook.xlsx.writeBuffer()
        const excelBlob = new Blob([buffer], { type: 'application/octet-stream' })

        const url = window.URL.createObjectURL(excelBlob)
        const a = document.createElement('a')
        a.href = url

        const currentDate = new Date()
        a.download = `${this.$t('driverranking')}-${currentDate.getDate()}.${
          currentDate.getMonth() + 1
        }.${currentDate.getFullYear()} ${currentDate.getHours()}_${currentDate.getMinutes()}_${currentDate.getSeconds()}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      },
      async exportAsPdf() {
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4',
        })

        doc.setFontSize(10)

        const startDate = new Date(this.searchObject.selectedStartDate)
        const endDate = new Date(this.searchObject.selectedEndDate)

        let currentY = 10

        doc.text(`${replaceSerbianChars(this.$t('startDate'))}: ${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()} 00:00:00`, 10, currentY)
        currentY += 8
        doc.text(`${this.$t('endDate')}: ${endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()} 00:00:00`, 10, currentY)
        currentY += 10

        const mainTableHeaders = [
          this.$t('driver'),
          this.$t('mileage'),
          this.$t('incidentType.HarshAcceleration'),
          this.$t('incidentType.HarshBreaking'),
          this.$t('incidentType.HarshCornering'),
          this.$t('incidentType.Speeding'),
          this.$t('drivingTimeExceeded'),
          this.$t('scoreboard'),
        ]

        const mainTableData = this.response.map((driver) => {
          const [firstName, ...lastName] = replaceSerbianChars(driver.driverName).split(' ')
          const formattedName = `${firstName}\n${lastName.join(' ')}`
          return [
            formattedName,
            driver.totalDistanceKm,
            driver.totalHarshAccelerationIncidents,
            driver.totalHarshBreakingIncidents,
            driver.totalHarshCorneringIncidents,
            driver.speedingViolationPoints,
            driver.drivingTimeExceeded,
            driver.negativePoints,
          ]
        })

        doc.autoTable({
          startY: currentY,
          head: [mainTableHeaders.map((header) => replaceSerbianChars(header))],
          body: mainTableData,
          styles: {
            font: 'helvetica',
            fontSize: 10,
            halign: 'center',
            textColor: [0, 0, 0],
            fillColor: [255, 255, 255],
          },
          bodyStyles: {
            fillColor: [255, 255, 255],
          },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [0, 0, 0],
          },
          didParseCell: (data) => {
            if (data.section === 'body') {
              const rowIndex = data.row.index
              const negativePoints = parseFloat(mainTableData[rowIndex][6].replace(",", "."))

              let bgColor = data.cell.styles.fillColor = [165, 214, 167]

              if (!isNaN(negativePoints) ) {
                if (negativePoints > 1.5) {
                  bgColor = [239, 154, 154]
                } else if (negativePoints >= 1) {
                  bgColor = [255, 245, 157]
                }
              }

              data.cell.styles.fillColor = bgColor
            }
          },
        })

        currentY = doc.lastAutoTable.finalY + 10

        this.response.forEach((driver, driverIndex) => {
          if (driverIndex > 0) {
            doc.addPage()
            currentY = 10
          }

          doc.text(`${replaceSerbianChars(this.$t('driver'))}: ${replaceSerbianChars(driver.driverName)}`, 10, currentY)
          currentY += 8
          doc.text(`${replaceSerbianChars(this.$t('startDate'))}: ${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()} 00:00:00`, 10, currentY)
          currentY += 8
          doc.text(`${this.$t('endDate')}: ${endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()} 00:00:00`, 10, currentY)
          currentY += 8
          doc.text(`${this.$t('negativePoints')}: ${driver.negativePoints || this.$t('noData')}`, 10, currentY)
          currentY += 10

          const driverTableHeaders = [
            this.$t('reportHeaders.Timestamp'),
            this.$t('incidentType.Incident type'),
            this.$t('graphs.Speed'),
            this.$t('incidentType.Incident value'),
            this.$t('isValid'),
            this.$t('showOnMap')
          ]

          const driverTableData = driver.incidents.map((incident) => {
            return [
              incident.timestamp,
              replaceSerbianChars(incident.incidentName),
              incident.speed,
              incident.incidentValue,
              incident.isFromZobs ? '' : incident.isValid ? this.$t('yes') : this.$t('no'),
              ''
            ]
          })

          doc.autoTable({
            startY: currentY,
            head: [driverTableHeaders],
            body: driverTableData,
            styles: {
              font: 'helvetica',
              fontSize: 10,
              halign: 'center',
              textColor: [0, 0, 0],
              fillColor: [255, 255, 255],
            },
            bodyStyles: {
              fillColor: [255, 255, 255],
            },
            headStyles: {
              fillColor: [230, 230, 230],
              textColor: [0, 0, 0],
            },
            didDrawCell: (data) => {
              if (data.column.index === 5 && data.cell.section === 'body') {
                const incident = driver.incidents[data.row.index]
                if (incident.isFromZobs) {
                  return;
                }
                const link = `https://www.google.com/maps/place/${incident.latitude},${incident.longitude}?hl=sr`
                doc.setTextColor(0, 0, 255)
                doc.textWithLink(this.$t('showOnMap'), data.cell.x + 10, data.cell.y + 4, { url: link })
              }
            },
          })

          currentY = doc.lastAutoTable.finalY + 10
        })

        const pdfBlob = doc.output('blob')
        const url = window.URL.createObjectURL(pdfBlob)
        const a = document.createElement('a')
        a.href = url

        const currentDate = new Date()
        a.download = `${this.$t('driverranking')}-${currentDate.getDate()}.${currentDate.getMonth() + 1}.${currentDate.getFullYear()} ${currentDate.getHours()}_${currentDate.getMinutes()}_${currentDate.getSeconds()}.pdf`
        a.click()
        window.URL.revokeObjectURL(url)
      }
    }
}
</script>

<style>
.data-container {
  height: 60vh;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.header-is-valid .v-input.v-input--dense > .v-input__control > .v-input__slot {
  margin-top: 0px;
}


.header-is-valid-checkbox .v-icon {
  color: white !important;
}
</style>